/**
 * Personer-sidens fire faner, som verdier.
 *
 * EGEN FIL OG IKKE I `PeoplePage.tsx`, av nøyaktig grunnen `performance-tabs.ts` finnes: ruta må
 * validere `?tab=` i kanten, og en import fra sidekomponenten ville dratt hele siden inn i
 * ruterens hovedbunt — og gjort `lazyRouteComponent` under den til pynt.
 */
import type { Actor } from '@tvitvi/shared';

import { allows } from '@/auth/actor';

export const PEOPLE_TAB_IDS = [
  'staffing',
  'resources',
  'participants',
  'volunteers',
  /* Persontypene §3.2: Leverandører, ved siden av Frivillige. Den samme fanen med `kind=supplier`. */
  'suppliers',
  'accreditation',
] as const;
export type PeopleTabId = (typeof PEOPLE_TAB_IDS)[number];

/** Fanene som kan skjules, og om leseren får se dem. «Stab og vakter» er alltid der. */
export interface PeopleTabAccess {
  participants: boolean;
  volunteers: boolean;
  suppliers: boolean;
  accreditation: boolean;
  resources: boolean;
}

export function peopleTabAccess(actor: Actor | null): PeopleTabAccess {
  return {
    participants: allows(actor, 'participation:list'),
    volunteers: allows(actor, 'volunteer:list'),
    /* Den samme modulen og de samme cellene som Frivillige. */
    suppliers: allows(actor, 'volunteer:list'),
    accreditation: allows(actor, 'accreditation:list'),
    resources: allows(actor, 'resource:list'),
  };
}

/**
 * Fanen siden faktisk står på. Her og ikke i siden fordi rutas loader må lande på den SAMME
 * fanen for å hente det fanen leser — to kopier av regelen ville hentet én fane og tegnet en annen.
 *
 * En ukjent verdi, eller en fane leseren ikke får åpne, faller stille tilbake til «Stab og vakter»
 * (QA-punkt 9): et gammelt bokmerke eller en lenke fra en kollega er ikke en feilsituasjon.
 */
export function resolvePeopleTab(raw: unknown, access: PeopleTabAccess): PeopleTabId {
  const requested: PeopleTabId =
    typeof raw === 'string' && (PEOPLE_TAB_IDS as readonly string[]).includes(raw)
      ? (raw as PeopleTabId)
      : 'staffing';
  return requested !== 'staffing' && !access[requested] ? 'staffing' : requested;
}
